/**
 * Timeline des programmes — packs de SKILLCARDS (migration 0289, lot F).
 * PUR, testé, partagé app ⇄ marketing.
 *
 * L'année scolaire va de septembre à août : un pack se place par son mois de
 * début (`start_month`, 1..12) puis par `position`. Un prof peut surcharger
 * l'ordre, le mois et masquer un pack pour une classe (`class_progressions`).
 * Seuil de maîtrise d'une carte : 80 (miroir de SKILLCARD_MASTERED_THRESHOLD).
 */

import {
  SCHOOL_LEVELS,
  SUBJECTS,
  UPCOMING_SUBJECTS,
  type SchoolLevel,
  type SubjectSlug,
} from '../index';

export const PACK_MASTERED_THRESHOLD = 80;

/** Mois de l'année scolaire, dans l'ordre (septembre → août). */
export const SCHOOL_MONTHS = [9, 10, 11, 12, 1, 2, 3, 4, 5, 6, 7, 8] as const;

const MONTH_SHORT = [
  'janv.',
  'févr.',
  'mars',
  'avr.',
  'mai',
  'juin',
  'juil.',
  'août',
  'sept.',
  'oct.',
  'nov.',
  'déc.',
] as const;
const MONTH_LONG = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
] as const;

export function monthShortLabel(month: number): string {
  return MONTH_SHORT[(month - 1 + 12) % 12] ?? '';
}
export function monthLongLabel(month: number): string {
  return MONTH_LONG[(month - 1 + 12) % 12] ?? '';
}

/** Rang dans l'année scolaire (septembre = 0 … août = 11). Miroir SQL `school_month_rank`. */
export function schoolMonthRank(month: number | null | undefined): number {
  return ((month ?? 9) + 3) % 12;
}

/** Icônes autorisées pour un pack (noms lucide, rendus par l'app et le site). */
export const PACK_ICONS = [
  'sparkles',
  'calculator',
  'hash',
  'superscript',
  'sigma',
  'scale',
  'triangle',
  'line-chart',
  'percent',
  'compass',
  'dices',
  'box',
  'code',
  'graduation-cap',
  'infinity',
  'trending-up',
  'function-square',
  'area-chart',
  'bar-chart',
] as const;
export type PackIcon = (typeof PACK_ICONS)[number];

export function packIcon(icon: string | null | undefined): PackIcon {
  return (PACK_ICONS as readonly string[]).includes(icon ?? '') ? (icon as PackIcon) : 'sparkles';
}

export interface PackLike {
  id: string;
  start_month: number;
  position: number;
  title: string;
}

/** Tri de l'année : mois scolaire, puis position, puis titre. */
export function sortPacks<T extends PackLike>(packs: readonly T[]): T[] {
  return [...packs].sort(
    (a, b) =>
      schoolMonthRank(a.start_month) - schoolMonthRank(b.start_month) ||
      a.position - b.position ||
      a.title.localeCompare(b.title, 'fr'),
  );
}

/**
 * Pack « en cours » à une date : le dernier dont le mois de début est passé
 * dans l'année scolaire (juillet / août = le dernier pack). -1 si aucun.
 */
export function currentPackIndex(packs: readonly PackLike[], today = new Date()): number {
  if (packs.length === 0) return -1;
  const now = schoolMonthRank(today.getMonth() + 1);
  let idx = -1;
  packs.forEach((p, i) => {
    if (schoolMonthRank(p.start_month) <= now) idx = i;
  });
  return idx === -1 ? 0 : idx;
}

/** % de cartes maîtrisées, ou null pour un pack sans carte. */
export function packMasteryPercent(mastered: number, total: number): number | null {
  if (!total || total <= 0) return null;
  return Math.round((Math.min(mastered, total) / total) * 100);
}

export type MasteryTone = 'empty' | 'none' | 'low' | 'mid' | 'high';

/** Teinte d'une case de la grille prof (et des barres élève). */
export function masteryTone(percent: number | null): MasteryTone {
  if (percent == null) return 'empty';
  if (percent <= 0) return 'none';
  if (percent < 40) return 'low';
  if (percent < PACK_MASTERED_THRESHOLD) return 'mid';
  return 'high';
}

/** Libellé de niveau (« 3ème », « Terminale »). */
export function levelLabel(level: string | null | undefined): string {
  return SCHOOL_LEVELS.find((l) => l.value === level)?.label ?? level ?? '';
}

/** Libellé de matière / spécialité (« Maths Spécialité »). */
export function subjectLabel(subject: string | null | undefined): string {
  return (
    SUBJECTS.find((s) => s.value === subject)?.label ??
    UPCOMING_SUBJECTS.find((s) => s.value === subject)?.label ??
    subject ??
    ''
  );
}

/** Matières (spécialités) proposées pour un niveau, d'après les packs publiés. */
export function subjectsWithPacks(
  packs: readonly { level: string; subject: string }[],
  level: string,
): SubjectSlug[] {
  const order = SUBJECTS.map((s) => s.value as string);
  return [...new Set(packs.filter((p) => p.level === level).map((p) => p.subject))]
    .sort((a, b) => order.indexOf(a) - order.indexOf(b))
    .map((s) => s as SubjectSlug);
}

/** Niveaux qui ont au moins un pack publié, dans l'ordre scolaire. */
export function levelsWithPacks(packs: readonly { level: string }[]): SchoolLevel[] {
  const present = new Set(packs.map((p) => p.level));
  return SCHOOL_LEVELS.map((l) => l.value).filter((v) => present.has(v));
}

export interface ClassPackOverride {
  pack_id: string;
  position: number;
  start_month: number | null;
  hidden: boolean;
}

/**
 * Nouvel ordre après déplacement d'un pack (prof) : renvoie la liste complète
 * des surcharges, positions 0..n-1 dans l'ordre affiché.
 */
export function movePack<T extends { id: string; start_month: number; hidden?: boolean }>(
  packs: readonly T[],
  index: number,
  delta: -1 | 1,
): ClassPackOverride[] {
  const list = [...packs];
  const target = index + delta;
  if (index >= 0 && target >= 0 && target < list.length) {
    const [item] = list.splice(index, 1);
    list.splice(target, 0, item!);
  }
  return list.map((p, i) => ({
    pack_id: p.id,
    position: i,
    start_month: p.start_month,
    hidden: !!p.hidden,
  }));
}

/** Slug de pack (titre → kebab, préfixé niveau + matière). */
export function packSlug(level: string, subject: string, title: string): string {
  const base = title
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
  return `${level}-${subject}-${base || 'pack'}`.replace(/-+/g, '-');
}

// ── Validité d'un PROGRAMME officiel (curriculum_programmes, migration 0292) ─
// Le programme porte la validité (rentrée, fin facultative) ; le pack porte une
// période INDICATIVE dans l'année (planning) : start_month → end_month.

export type ProgrammeStatus = 'active' | 'upcoming' | 'ended';

/** Date locale AAAA-MM-JJ (comparaisons de dates sans fuseau). */
export function isoDay(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/** Miroir SQL `curriculum_programme_is_active` : from <= jour <= until (until facultatif). */
export function programmeStatus(
  from: string,
  until: string | null | undefined,
  today = new Date(),
): ProgrammeStatus {
  const d = isoDay(today);
  if (from > d) return 'upcoming';
  if (until && d > until) return 'ended';
  return 'active';
}

export function isProgrammeActive(
  from: string,
  until: string | null | undefined,
  today = new Date(),
) {
  return programmeStatus(from, until, today) === 'active';
}

const frDate = (iso: string) => {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

/** « En vigueur depuis la rentrée 2025 », « À partir de la rentrée 2028 », « Terminé le 31/08/2028 ». */
export function programmeLabel(
  from: string,
  until: string | null | undefined,
  today = new Date(),
): string {
  const status = programmeStatus(from, until, today);
  const year = from.slice(0, 4);
  if (status === 'upcoming') return `À partir de la rentrée ${year}`;
  if (status === 'ended') return `Programme terminé le ${frDate(until!)}`;
  return until
    ? `En vigueur depuis la rentrée ${year}, jusqu’au ${frDate(until)}`
    : `En vigueur depuis la rentrée ${year}`;
}

/** Période valide : rentrée obligatoire (AAAA-MM-JJ), fin facultative et postérieure. */
export function programmePeriodIssue(
  from: string,
  until: string | null | undefined,
): string | null {
  const re = /^\d{4}-\d{2}-\d{2}$/;
  if (!re.test(from)) return 'Indique la date de rentrée du programme.';
  if (until && !re.test(until)) return 'Date de fin invalide.';
  if (until && until < from) return 'La date de fin doit suivre la date de rentrée.';
  return null;
}

/** Rentrée par défaut d'une année scolaire (1er septembre). */
export function rentreeOf(year: number): string {
  return `${year}-09-01`;
}

/** Nombre de packs (autres que `exceptPackId`) contenant chaque carte — many-to-many. */
export function cardPackCounts(
  packs: readonly { id: string; cards: readonly { id: string }[] }[],
  exceptPackId?: string | null,
): Map<string, number> {
  const out = new Map<string, number>();
  for (const p of packs) {
    if (p.id === exceptPackId) continue;
    for (const c of p.cards) out.set(c.id, (out.get(c.id) ?? 0) + 1);
  }
  return out;
}

/** Période indicative d'un pack dans l'année : « sept. », « sept. – oct. ». */
export function packPeriodLabel(start: number, end?: number | null): string {
  if (!end || end === start) return monthShortLabel(start);
  return `${monthShortLabel(start)} – ${monthShortLabel(end)}`;
}

/** Fin indicative valide : même mois ou plus tard dans l'année scolaire. */
export function packPeriodIssue(start: number, end?: number | null): string | null {
  if (end == null) return null;
  return schoolMonthRank(end) < schoolMonthRank(start)
    ? 'La fin de la période doit suivre son début dans l’année scolaire.'
    : null;
}

export interface ProgrammeLike {
  id: string;
  valid_from: string;
  valid_until: string | null;
}

/** Programme en vigueur à une date (le plus récent s'il y en a plusieurs), sinon le prochain à venir. */
export function pickProgramme<T extends ProgrammeLike>(
  programmes: readonly T[],
  today = new Date(),
): T | null {
  const sorted = [...programmes].sort((a, b) => b.valid_from.localeCompare(a.valid_from));
  return (
    sorted.find((p) => isProgrammeActive(p.valid_from, p.valid_until, today)) ??
    [...sorted]
      .reverse()
      .find((p) => programmeStatus(p.valid_from, p.valid_until, today) === 'upcoming') ??
    sorted[0] ??
    null
  );
}
